import React from "react";
import { Box, Divider, Theme, Typography } from "@mui/material";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import { BodypartType } from "@growth-machine-llc/stridist-api-client";
import { BodypartTypeLabels } from "../../exercise-library/constants";
import { createCrossLinesGradient } from "../WorkoutActivityCalendar";

const OthersLabel = "Others";
const BODYPARTS_TO_SHOW_COUNT = 2;

interface ThemeOptions {
  isTooltip?: boolean;
}

const useStyles = makeStyles<Theme, ThemeOptions>((theme) => ({
  barContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
  },
  bar: {
    height: 10,
    borderRadius: 2,
  },
  barSelected: {
    backgroundColor: theme.palette.primary.main,
    backgroundImage: "none",
  },
  barOthers: {
    backgroundColor: theme.palette.grey[300],
    backgroundImage: "none",
  },
  barPattern: {
    backgroundColor: "transparent",
    backgroundImage: createCrossLinesGradient(theme.palette.primary.main),
  },
  bodypartsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: ({ isTooltip }) => theme.spacing(isTooltip ? 0.5 : 1),
  },

  bodypartLabel: {
    fontWeight: 500,
    lineHeight: 1,
  },

  bodypartHeaderLabel: {
    fontWeight: 500,
    alignSelf: "flex-start",
    margin: ({ isTooltip }) => (isTooltip ? 0 : theme.spacing(1, 0)),
  },

  divider: {
    margin: ({ isTooltip }) => theme.spacing(isTooltip ? 0.5 : 1, 0),
  },

  bodypartItem: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
}));

interface Props {
  bodyparts: string[];
  selectedBodypart: BodypartType;
  isTooltip?: boolean;
}

const BodypartBars: React.FC<Props> = ({
  bodyparts,
  selectedBodypart,
  isTooltip = false,
}) => {
  const s = useStyles({ isTooltip: isTooltip });

  const aggregated = React.useMemo(() => {
    if (!bodyparts || bodyparts.length === 0) return [];

    const grouped: Record<string, number> = {};
    bodyparts.forEach((bpName) => {
      if (!bpName || !BodypartTypeLabels[bpName]) return;
      grouped[BodypartTypeLabels[bpName]] =
        (grouped[BodypartTypeLabels[bpName]] || 0) + 1;
    });

    const total = Object.values(grouped).reduce((sum, c) => sum + c, 0);
    const arr = Object.entries(grouped)
      .map(([name, count]) => ({
        name,
        count,
        percentage: Math.round((count / total) * 100),
      }))
      .sort((a, b) => b.percentage - a.percentage);

    const top = arr.slice(0, BODYPARTS_TO_SHOW_COUNT);
    const others = arr.slice(BODYPARTS_TO_SHOW_COUNT);

    const othersCount = others.reduce((sum, bp) => sum + bp.count, 0);

    const selectedLabel = BodypartTypeLabels[selectedBodypart];
    const isSelectedPresent = !!arr.find((bp) => bp.name === selectedLabel);
    const isSelectedInTop = top.some((bp) => bp.name === selectedLabel);

    if (othersCount > 0) {
      const othersPercentage = Math.round((othersCount / total) * 100);

      if (isSelectedPresent) {
        const othersName = isSelectedInTop
          ? OthersLabel
          : `${selectedLabel}, ${OthersLabel.toLowerCase()}`;

        top.push({
          name: othersName,
          count: othersCount,
          percentage: othersPercentage,
        });
      } else {
        top.push({
          name: OthersLabel,
          count: othersCount,
          percentage: othersPercentage,
        });
      }
    }

    return top.sort((a, b) => b.percentage - a.percentage);
  }, [bodyparts, selectedBodypart]);

  if (aggregated.length === 0) return null;

  return (
    <>
      <Divider className={s.divider} />
      <Typography fontSize={14} className={s.bodypartHeaderLabel}>
        Bodyparts:
      </Typography>
      <Box className={s.bodypartsContainer}>
        {aggregated.map((bp) => (
          <Box key={bp.name} display={"flex"} flexDirection="column" gap={0.5}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle2" className={s.bodypartLabel}>
                {bp.name}
              </Typography>
            </Box>
            <Box className={s.barContainer}>
              <Box
                className={clsx(
                  s.bar,
                  bp.name === BodypartTypeLabels[selectedBodypart]
                    ? s.barSelected
                    : bp.name
                          .toLocaleLowerCase()
                          .includes(OthersLabel.toLowerCase())
                      ? s.barOthers
                      : s.barPattern,
                )}
                style={{ width: `${bp.percentage}%` }}
              />
              <Typography variant="subtitle2" fontWeight={500}>
                {bp.percentage}%
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default BodypartBars;
